import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';

import { cn } from '../lib/cn';

/** `Field`가 자식 control에 넘기는 접근성 배선. */
export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  required?: boolean;
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  /**
   * control 하나. 함수로 주면 `{ id, aria-describedby, aria-invalid, required }`를
   * 직접 넘겨받는다 — `Select.Trigger`처럼 단일 엘리먼트가 아닌 조합에 쓴다.
   * 엘리먼트 하나로 주면 같은 값을 `cloneElement`로 얹는다.
   */
  children: ReactNode | ((control: FieldControlProps) => ReactNode);
}

/**
 * 라벨 + control + 힌트 + 오류를 `id`·`aria-describedby`·`aria-invalid`로 잇는다.
 * `error`는 제출 뒤 서버 400 필드 오류다(E14 §7) — native 검증 상태가 아니라서 `role="alert"`로 알린다.
 */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const controlId = useId();
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;

  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  const control: FieldControlProps = {
    id: controlId,
    'aria-describedby': describedBy,
    ...(error ? { 'aria-invalid': true as const } : {}),
    ...(required ? { required: true } : {}),
  };

  return (
    <div className={cn('gap-tight flex flex-col', className)}>
      <label htmlFor={controlId} className="text-fg text-sm font-semibold">
        {label}
      </label>
      {typeof children === 'function'
        ? children(control)
        : isValidElement(children)
          ? cloneElement(children as ReactElement<FieldControlProps>, control)
          : children}
      {hint ? (
        <p id={hintId} className="text-ink-muted text-xs">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-danger text-xs">
          {error}
        </p>
      ) : null}
    </div>
  );
}
